import { catalog } from '../core/catalog.js';
import { epg, programProgress } from '../core/epg.js';
import { PlayerCore } from '../core/player.js';
import { nativePreviewCore, physicalRect } from '../core/native-player.js';
import { t } from '../core/i18n.js';
import { device } from '../core/device.js';
import { escapeHtml, formatClock, cleanName } from '../core/utils.js';
import { previewMuted, setPreviewMuted, onPreviewMute } from '../core/preview-sound.js';
import { img, enhance, programMeta } from './render.js';
import { splitProgramDesc } from '../core/epg-desc.js';
import { previewChannel } from '../core/preview-variant.js';
import { warmup } from '../core/warmup.js';

/* Vista «Panel» de TV en directo (como en las aplicaciones de IPTV de las operadoras): tres columnas a pantalla completa —las categorías con su
   número de canales, los canales de la categoría con lo que dan ahora y, a la derecha, una ventanita con el canal y su programación—.
   Recorrer los canales (flechas del mando, o tocar uno) cambia la ventanita; pulsar OK / tocar de nuevo el canal elegido lo abre a pantalla
   completa. El sonido sigue el estado general de las vistas previas. */

const SWITCH_MS = 300;      // quieto sobre un canal este rato y la ventanita salta a él (la lista ya se ha pedido al proveedor al enfocarlo)
const GIVE_UP_MS = 25000;   // si no ha empezado a sonar, se avisa
const CHUNK = 80;           // canales por tanda en la columna
const ALL = '__all__';

const fold = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');

const rowHtml = (ch) => {
    const nn = epg.nowNext(ch.epgId);
    return `<button type="button" class="pn-ch" data-pn-ch="${escapeHtml(String(ch.id))}" aria-label="${escapeHtml(cleanName(ch.name))}">
        <span class="pn-logo">${img(ch.logo, '')}<span class="mi pn-fallback">live_tv</span></span>
        <span class="pn-body">
            <span class="pn-name">${escapeHtml(cleanName(ch.name))}</span>
            <span class="pn-now" data-pn-now>${escapeHtml(nn.now ? nn.now.title : t('live.noEpg'))}</span>
            <i class="pn-bar" data-pn-bar ${nn.now ? '' : 'hidden'}><i style="width:${nn.now ? programProgress(nn.now) : 0}%"></i></i>
        </span>
    </button>`;
};

/** Crea la vista dentro de `host` (ocupa toda la pantalla). `onOpen(id)` abre un canal a pantalla completa; `onExit()` deja la vista. */
export function createPanel(host, { onOpen, onExit, onChannelMenu = null, onCategoryMenu = null }) {
    let groups = [];
    let catId = '';        // categoría elegida ('' = la del canal que se vio último, o la primera)
    let catQuery = '';
    let items = [];        // canales de la categoría elegida
    let shown = 0;
    let sel = null;
    let selTimer = null;
    let giveUp = null;
    let core = null;
    let video = null;
    let syncTimer = null;
    let nativeMode = false;
    let alive = true;
    let paused = false;
    let pendingFocus = null; // tras reordenar se repinta: el foco vuelve a lo que se movió
    const offs = [];

    host.classList.add('lstage-host');
    document.documentElement.classList.add('panel-open'); // la barra de navegación se queda a la vista (arriba o en el lateral, según el aspecto): desde aquí se va a Inicio, Películas, Series…
    host.innerHTML = `<div class="lpanel" data-lpanel>
        <div class="lpanel-head">
            <div class="lpanel-title"><span class="mi">live_tv</span><span>${escapeHtml(t('live.title'))}</span></div>
            <div class="lpanel-hint tv-only"><span class="mi">swap_vert</span>${escapeHtml(t('live.panelOrderHint'))}</div>
            <button type="button" class="lstage-exit lpanel-exit" data-pn-exit aria-label="${escapeHtml(t('live.stageExit'))}"><span class="mi">grid_view</span><span>${escapeHtml(t('live.stageExit'))}</span></button>
        </div>
        <div class="lpanel-cols">
            <div class="lpanel-cats">
                <label class="lpanel-search"><span class="mi">search</span><input type="search" data-pn-catq placeholder="${escapeHtml(t('live.panelSearch'))}" autocomplete="off" autocapitalize="off"/></label>
                <div class="lpanel-catlist" data-pn-cats data-nav-list></div>
            </div>
            <div class="lpanel-chs" data-pn-chs data-nav-list></div>
            <div class="lpanel-side">
                <div class="lpanel-video" data-pn-video>
                    <div class="spinner lstage-wait" data-pn-wait hidden></div>
                    <button type="button" class="lstage-sound" data-pn-sound hidden aria-label="${escapeHtml(t('preview.sound'))}"><span class="mi">volume_off</span></button>
                </div>
                <div class="lpanel-info" data-pn-info></div>
            </div>
        </div>
    </div>`;
    const panelEl = host.querySelector('[data-lpanel]');
    const catsEl = host.querySelector('[data-pn-cats]');
    const chsEl = host.querySelector('[data-pn-chs]');
    const videoBox = host.querySelector('[data-pn-video]');
    const wait = host.querySelector('[data-pn-wait]');
    const soundBtn = host.querySelector('[data-pn-sound]');
    const infoEl = host.querySelector('[data-pn-info]');
    const catQ = host.querySelector('[data-pn-catq]');

    // «Atrás» (mando, Esc, tecla del aparato) deja la vista antes de salir de la página.
    const stageBack = () => { if (paused || !alive) return false; onExit(); return true; };
    if (window.IPTV) window.IPTV.stageBack = stageBack;

    const isTV = () => (window.IPTV && window.IPTV.device && window.IPTV.device.isTV) || document.documentElement.classList.contains('mode-tv');
    const byId = (id) => items.find((c) => String(c.id) === String(id)) || null;
    const esc = (id) => (window.CSS && CSS.escape ? CSS.escape(String(id)) : String(id));
    const rowOf = (id) => chsEl.querySelector(`[data-pn-ch="${esc(id)}"]`);

    /* ---------- categorías ---------- */
    const allCount = () => groups.reduce((n, g) => n + g.items.length, 0);
    const catList = () => [{ id: ALL, name: t('live.panelAll'), items: null, count: allCount() }].concat(groups.map((g) => ({ id: g.id, name: g.name, items: g.items, count: g.items.length })));
    const itemsOf = (id) => {
        if (id === ALL) return groups.reduce((a, g) => a.concat(g.items), []);
        const g = groups.find((x) => x.id === id);
        return g ? g.items : [];
    };
    const paintCats = () => {
        const q = fold(catQuery);
        const list = catList().filter((c) => !q || fold(c.name).indexOf(q) !== -1);
        catsEl.innerHTML = list.map((c) => `<button type="button" class="pn-cat ${c.id === catId ? 'is-selected' : ''}" data-pn-cat="${escapeHtml(c.id)}"><span class="pn-cat-name">${escapeHtml(c.name)}</span><b>${c.count.toLocaleString()}</b></button>`).join('')
            || `<p class="lpanel-empty">${escapeHtml(t('common.noResults'))}</p>`;
    };

    /* ---------- canales ---------- */
    const paintChannels = (keepSel) => {
        const upto = Math.min(CHUNK, items.length);
        shown = upto;
        chsEl.innerHTML = items.slice(0, upto).map(rowHtml).join('') || `<p class="lpanel-empty">${escapeHtml(t('common.noResults'))}</p>`;
        chsEl.scrollTop = 0;
        enhance(chsEl);
        if (keepSel) markSelected();
    };
    const appendMore = () => {
        if (shown >= items.length) return;
        const next = items.slice(shown, shown + CHUNK);
        shown += next.length;
        chsEl.insertAdjacentHTML('beforeend', next.map(rowHtml).join(''));
        enhance(chsEl);
        if (sel) markSelected();
    };
    chsEl.addEventListener('scroll', () => { if (chsEl.scrollTop + chsEl.clientHeight > chsEl.scrollHeight - 600) appendMore(); }, { passive: true });
    const markSelected = () => {
        chsEl.querySelectorAll('.pn-ch.is-selected').forEach((x) => x.classList.remove('is-selected'));
        const row = sel && rowOf(sel.id);
        if (row) row.classList.add('is-selected');
        return row;
    };
    /** Lo que dan ahora, al día (llega la guía) sin rehacer la lista: así no se pierde el foco. */
    const refreshNow = () => {
        chsEl.querySelectorAll('[data-pn-ch]').forEach((row) => {
            const ch = byId(row.getAttribute('data-pn-ch'));
            if (!ch) return;
            const nn = epg.nowNext(ch.epgId);
            const now = row.querySelector('[data-pn-now]');
            const bar = row.querySelector('[data-pn-bar]');
            if (now) now.textContent = nn.now ? nn.now.title : t('live.noEpg');
            if (bar) { bar.hidden = !nn.now; const i = bar.firstElementChild; if (i) i.style.width = (nn.now ? programProgress(nn.now) : 0) + '%'; }
        });
    };

    /** Tras reordenar se repinta la lista: el foco vuelve a lo que se movió. Se espera a que se cierre el menú (si no, la página de detrás se lo queda). */
    const applyPendingFocus = () => {
        if (!pendingFocus) return false;
        const want = pendingFocus;
        pendingFocus = null;
        const go = () => {
            if (!alive || paused) return;
            const el = want.cat ? catsEl.querySelector(`[data-pn-cat="${esc(want.cat)}"]`) : rowOf(want.ch);
            if (!el) return;
            el.scrollIntoView({ block: 'nearest' });
            if (isTV() && window.IPTV && window.IPTV.tvnav) window.IPTV.tvnav.focus(el, { scroll: false });
        };
        go();
        setTimeout(go, 300);
        return true;
    };

    const chooseCategory = (id, { keep = false } = {}) => {
        catId = id;
        items = itemsOf(id);
        catsEl.querySelectorAll('.pn-cat.is-selected').forEach((x) => x.classList.remove('is-selected'));
        const b = catsEl.querySelector(`[data-pn-cat="${esc(id)}"]`);
        if (b) b.classList.add('is-selected');
        paintChannels(keep);
    };

    /* ---------- ventanita e información ---------- */
    const renderInfo = (note = '') => {
        if (!sel) { infoEl.innerHTML = ''; return; }
        const nn = epg.nowNext(sel.epgId);
        const now = nn.now;
        const prog = now ? splitProgramDesc(now.desc) : null;
        infoEl.innerHTML = `<div class="lst-kicker">${escapeHtml(cleanName(sel.name))}</div>
            <div class="lst-title">${escapeHtml(now ? now.title : t('live.noEpg'))}</div>
            ${now ? `<div class="lst-sub">${escapeHtml(formatClock(now.start))} - ${escapeHtml(formatClock(now.stop))}</div><div class="lst-progress"><i style="width:${programProgress(now)}%"></i></div>` : ''}
            ${prog ? programMeta(prog) : ''}
            ${prog && prog.plot ? `<p class="lst-desc">${escapeHtml(prog.plot)}</p>` : ''}
            ${nn.next ? `<div class="lpanel-next"><span>${escapeHtml(t('live.panelNext'))}</span> ${escapeHtml(formatClock(nn.next.start))} · ${escapeHtml(nn.next.title)}</div>` : ''}
            ${note ? `<p class="lst-note"><span class="mi">error_outline</span>${escapeHtml(note)}</p>` : ''}`;
    };
    const vmuted = () => (core && core.video ? !!core.video.muted : previewMuted());
    const syncSound = () => { if (video) soundBtn.querySelector('.mi').textContent = vmuted() ? 'volume_off' : 'volume_up'; };
    const stopVideo = () => {
        clearTimeout(giveUp);
        clearInterval(syncTimer);
        if (core) { try { core.destroy(); } catch (e) { /* ya suelto */ } }
        core = null;
        if (video && video.parentNode) video.parentNode.removeChild(video);
        video = null;
        wait.hidden = true;
        soundBtn.hidden = true;
    };
    /** Un canal nuevo en la ventanita. En la app de Android se reutiliza el mismo reproductor nativo (cambiar de canal es una carga nueva, no
        montarlo de cero: es lo que lo hacía lento y lo que dejaba la imagen detrás de la página); en el navegador, uno por canal. */
    const play = (ch) => {
        if (!alive || paused || !ch || !videoBox.offsetWidth) return; // sin ventanita a la vista (pantalla estrecha) no se gasta una conexión
        let url = '';
        try { url = catalog.liveUrl(previewChannel(ch, { big: false })); } catch (e) { url = ''; }
        if (!url) { stopVideo(); renderInfo(t('live.stageError')); return; }
        clearTimeout(giveUp);
        const reuse = !!(core && nativeMode && video);
        if (!reuse) {
            stopVideo();
            // En la app de Android el canal lo pinta el reproductor nativo en un rectángulo de la pantalla; si no, el reproductor del navegador.
            const ncore = nativePreviewCore();
            nativeMode = !!ncore;
            video = document.createElement(ncore ? 'div' : 'video');
            video.className = 'lpanel-v';
            if (!ncore) {
                video.muted = previewMuted();
                video.playsInline = true;
                video.setAttribute('playsinline', '');
            }
            videoBox.insertBefore(video, videoBox.firstChild);
            core = ncore || new PlayerCore(video);
            const c = core;
            c.on('playing', () => { if (core !== c) return; clearTimeout(giveUp); wait.hidden = true; video.classList.add('is-playing'); soundBtn.hidden = false; syncSound(); renderInfo(); });
            c.on('autoplayblocked', () => { if (core !== c) return; c.video.muted = true; syncSound(); if (!ncore) video.play().catch(() => {}); });
            c.on('error', () => { if (core === c) failed(); });
            if (ncore) {
                let last = '';
                syncTimer = setInterval(() => { if (core !== c) return; const r = physicalRect(videoBox); const k = r.join(','); if (k !== last) { last = k; c.setRect(r); } }, 250);
            }
        }
        video.classList.remove('is-playing');
        soundBtn.hidden = true;
        wait.hidden = false;
        const c = core;
        giveUp = setTimeout(() => { if (core === c) failed(); }, GIVE_UP_MS);
        c.load({ url, live: true, muted: previewMuted(), autoplay: true, rect: nativeMode ? physicalRect(videoBox) : null, layer: 'front', cssClass: '' }).catch(() => { if (core === c) failed(); });
    };
    const failed = () => { wait.hidden = true; renderInfo(t('live.stageError')); };

    /** Elige un canal: la información cambia al momento; la ventanita, tras un instante (o ya, si se toca). */
    const select = (ch, { now = false } = {}) => {
        if (!ch) return;
        const same = sel && String(sel.id) === String(ch.id);
        sel = ch;
        markSelected();
        renderInfo();
        clearTimeout(selTimer);
        if (same && video) return;
        warmup.request('live', previewChannel(ch, { big: false }).id);
        selTimer = setTimeout(() => play(ch), now ? 0 : SWITCH_MS);
    };

    // Con mando, el foco recorre los canales y la ventanita lo sigue; con el ratón o el dedo, tocar un canal lo elige y tocarlo de nuevo lo abre.
    chsEl.addEventListener('nav:focus', (e) => {
        const row = e.target.closest && e.target.closest('[data-pn-ch]');
        if (row && !(sel && String(sel.id) === row.getAttribute('data-pn-ch'))) select(byId(row.getAttribute('data-pn-ch')));
    });
    host.addEventListener('click', (e) => {
        const row = e.target.closest('[data-pn-ch]');
        if (row) {
            const ch = byId(row.getAttribute('data-pn-ch'));
            if (!ch) return;
            if (sel && String(sel.id) === String(ch.id) && video) onOpen(ch.id);
            else select(ch, { now: true });
            return;
        }
        const cat = e.target.closest('[data-pn-cat]');
        if (cat) { chooseCategory(cat.getAttribute('data-pn-cat')); return; }
        if (e.target.closest('[data-pn-exit]')) { onExit(); return; }
        if (e.target.closest('.lpanel-v')) { if (sel) onOpen(sel.id); return; }
        if (e.target.closest('[data-pn-sound]') && video) setPreviewMuted(!vmuted());
    });
    // Pulsación larga (OK mantenido con el mando, dedo o botón derecho): menú para ordenar categorías y canales.
    host.addEventListener('pn:longpress', (e) => {
        const row = e.target.closest && e.target.closest('[data-pn-ch]');
        if (row && onChannelMenu) { const ch = byId(row.getAttribute('data-pn-ch')); if (ch) onChannelMenu(ch, catId, items.map((c) => String(c.id))); return; }
        const cat = e.target.closest && e.target.closest('[data-pn-cat]');
        if (cat && onCategoryMenu) {
            const id = cat.getAttribute('data-pn-cat');
            if (id === ALL) return;
            const g = groups.find((x) => x.id === id);
            if (g) onCategoryMenu({ id: g.id, name: g.name }, groups.map((x) => String(x.id)));
        }
    });
    catQ.addEventListener('input', () => { catQuery = catQ.value.trim(); paintCats(); });
    offs.push(onPreviewMute((m) => { if (core && core.video) { core.video.muted = m; syncSound(); } }));

    // Con el reproductor a pantalla completa abierto, la vista se calla (si no, serían dos conexiones al proveedor).
    const player = window.IPTV && window.IPTV.player;
    if (player) {
        offs.push(player.on('open', () => { paused = true; clearTimeout(selTimer); stopVideo(); }));
        offs.push(player.on('close', () => {
            paused = false;
            const cur = player.mode === 'live' ? byId(player.item && player.item.id) : null;
            if (cur) { sel = cur; markSelected(); renderInfo(); }
            setTimeout(() => {
                if (!alive) return;
                const row = sel && rowOf(sel.id);
                if (row) { row.scrollIntoView({ block: 'nearest' }); if (isTV() && window.IPTV.tvnav) window.IPTV.tvnav.focus(row, { scroll: false }); }
                play(sel);
            }, 150);
        }));
    }

    return {
        /** Pone las categorías (con sus canales, en su orden). Si la categoría y el canal elegidos siguen ahí, no se tocan; si no, se elige la del último canal visto (o la primera). */
        setData(list) {
            groups = list || [];
            paintCats();
            const stillThere = catId === ALL || groups.some((g) => g.id === catId);
            if (stillThere && catId) {
                items = itemsOf(catId);
                paintChannels(true);
                if (sel && byId(sel.id)) {
                    renderInfo();
                    applyPendingFocus();
                    return;
                }
            } else {
                const recent = (catalog.recent || []).map(String);
                const all = itemsOf(ALL);
                const last = recent.map((id) => all.find((c) => String(c.id) === id)).find(Boolean);
                const g = last ? groups.find((x) => x.items.indexOf(last) !== -1) : groups[0];
                chooseCategory(g ? g.id : ALL);
                sel = last && byId(last.id) ? last : null;
            }
            if (applyPendingFocus()) return;
            const first = sel || items[0];
            select(first, { now: true });
            const row = first && rowOf(first.id);
            if (row && isTV() && window.IPTV && window.IPTV.tvnav) setTimeout(() => { if (alive && !paused && row.isConnected) window.IPTV.tvnav.focus(row, { scroll: false }); }, 80);
        },
        /** Tras reordenar, la lista se repinta: el foco vuelve a la categoría o canal que se movió. */
        focusAfter(what) { pendingFocus = what || null; },
        /** Pone al día lo que dan ahora (llega la guía de programas) sin tocar la ventanita. */
        refreshInfo() { refreshNow(); if (sel && !infoEl.querySelector('.lst-note')) renderInfo(); },
        /** Se esconde la página (otra pestaña): la ventanita se para; al volver, sigue. */
        pause() { paused = true; panelEl.hidden = true; document.documentElement.classList.remove('panel-open'); clearTimeout(selTimer); stopVideo(); },
        resume() { if (!paused) return; paused = false; panelEl.hidden = false; document.documentElement.classList.add('panel-open'); play(sel); },
        destroy() {
            alive = false;
            clearTimeout(selTimer);
            stopVideo();
            if (window.IPTV && window.IPTV.stageBack === stageBack) window.IPTV.stageBack = null;
            offs.splice(0).forEach((fn) => { try { fn(); } catch (e) { /* ignore */ } });
            document.documentElement.classList.remove('panel-open');
            host.classList.remove('lstage-host');
            host.innerHTML = '';
        }
    };
}
